
import img0 from './Images/Schema_Copy.png';
import img1 from './Images/Schema_Copy_Options.png';
import img2 from './Images/Schema_Object_Types.png';
import img3 from './Images/Schema_Selection.png';
import img4 from './Images/Schema_Copy_Summary.png';
import img5 from './Images/Migrate_Encrypt_Data.png';
import img6 from './Images/Connect_Target_Database.png';
import img7 from './Images/Select_Tables.png';
import img8 from './Images/Migration_InProgress.png';
import img9 from './Images/Migration_Completed.png';
  
const content = `# Migrate and Encrypt Oracle Databases

The OnData Encryption Tool can be used to migrate and encrypt data in certain schemas from one Oracle database to another. For general information on how to use 
the OnData Encryption Tool to encrypt and obfuscate sensitive data in various types of databases, including Oracle databases, please refer to 
[**Use OnData Encryption Tool**](reference_doc#securedb/ondata_encryption/use_ondata_encryption_tool). This document focuses on the particular scenario of migrating and encrypting data in certain 
schemas from one Oracle database to another. In this case, the \`Migrate and Encrypt Date\` option is selected on the \`Encryption Settings\` page of the OnData 
Encryption Tool. Additional prerequisites are required in order to do the migration and encryption at the same time. 

## 1. Prerequisites
Here are the prerequisites for using the OnData Encryption Tool to migrate and encrypt data in certain schemas from a source Oracle database to a target 
Oracle database.

### 1.1 User Permissions for Source Database
For the source Oracle database, the database user must have the following permissions assigned at a minimum:
  - CREATE ANY PROCEDURE
  - CREATE ANY TRIGGER
  - CREATE SESSION
  - EXECUTE ANY TYPE
  - SELECT ANY DICTIONARY
  - SELECT ANY TABLE
  - SELECT_CATALOG_ROLE

### 1.2 User Permissions for Target Database
For the target Oracle database, the database user must have the following permissions assigned at a minimum:
  - ALTER ANY INDEX
  - ALTER ANY MATERIALIZED VIEW
  - ALTER ANY SEQUENCE
  - ALTER ANY TABLE
  - ALTER ANY TRIGGER
  - CREATE ANY INDEX
  - CREATE ANY SEQUENCE
  - CREATE ANY TABLE
  - CREATE ANY TRIGGER
  - CREATE DATABASE LINK
  - CREATE SESSION
  - DELETE ANY TABLE
  - DROP ANY INDEX
  - DROP ANY SEQUENCE
  - DROP ANY TABLE
  - EXECUTE ANY TYPE
  - INSERT ANY TABLE
  - LOCK ANY TABLE
  - SELECT ANY DICTIONARY
  - SELECT ANY SEQUENCE
  - SELECT ANY TABLE
  - UPDATE ANY TABLE

### 1.3 Database Link from Target Database to Source Database
To migrate and encrypt data from the source database to the target database, a database link has to be established from the target database to 
the source database. The command line for creating such database link is illustrated below.

\`\`\`Bash
CREATE DATABASE LINK {database link name} 
    CONNECT TO {source username} IDENTIFIED BY "{source user's password}" 
    USING '(DESCRIPTION =
        (ADDRESS_LIST = (ADDRESS = (PROTOCOL = TCP)(HOST = {source database IP address})(PORT = {source database port})))
        (CONNECT_DATA = (SERVICE_NAME = {source database name})))';

\`\`\`

## 2. Step-by-Step Instructions
Assuming all prerequisites are met, we can copy the desired database schema from the source database to the target database, and run the OnData Encryption 
Tool to migrate and encrypt data from the source database to the target database.

### 2.1 Copy Database Schema
There are two methods to copy the desired database schema from the source database to the target database. 

#### Use expdb and impdp utilities
a. Use the following script to export the desired schemas from the source database as a dump file.
\`\`\`Bash
expdp {username}/{password}@{server IP address}:{port}/{source database name} 
    schemas={list of desired schemas} 
    directory=ORACLE_BASE 
    dumpfile={dump file name}.dmp 
    CONTENT=METADATA_ONLY 
    logfile={logfile name}.log 

\`\`\`

b. Copy the dump file to the target environment and use the following script to import the schemas into the target database.
\`\`\`Bash
impdp {username}/{password}@{server IP address}:{port}/{target database name} 
    schemas={list of desired schemas} 
    directory=ORACLE_BASE 
    dumpfile={dump file name}.dmp 
    CONTENT=METADATA_ONLY 
    logfile={logfile name}.log 
    remap_tablespace={source database name}:{target database name} 

\`\`\`
For more information on how to use such utilities, please refer to the Oracle documentation:
   * [Oracle Data Dump Export](https://docs.oracle.com/en/database/oracle/oracle-database/19/sutil/oracle-data-pump-export-utility.html)
   * [Oracle Data Dump Import](https://docs.oracle.com/en/database/oracle/oracle-database/19/sutil/datapump-import-utility.html)

#### Use Oracle SQL Developer tool
The Oracle SQL Developer tool can be used to copy the database schema from the source database to the target database. Below are the steps.
a. Launch Oracle SQL Developer tool at the target database machine and connect to both the source database and target database.  
b. If the schemas to be migrated do not exist in the target database yet, create empty schemas, with the same names as the source schemas to be migrated, at the target 
database with unlimited quota for the table space.  
c. Select the \`Tools\` menu from SQL Developer and choose the \`Database Copy...\` item.  

![Schema_Copy](${ img0 })  

d. Choose the connections to the source database and the target database. Select the options of \`Schema Copy\` and \`Replace Existing Destination Objects\`. 
De-select the options of \`Ignore Storage, Partitioning and Tablespace\` and \`Copy Data\`. Click on the \`Next\` button.  

![Schema Copy Options](${ img1 })  

e. Select all \`Standard Object types\` as default and click on the \`Next\` button.  

![Schema Object Types](${ img2 })  

f. Select the schemas to be copied and select the option of \`Proceed to summary\` and click on \`Next\`.  

![Schema Selection](${ img3 })  

g. The \`Copy Summary\` is displayed. Select the \`Finish\` button to copy the schemas from the source database to the target database.  

![Schema Copy Summary](${ img4 })  

Once the schemas to be migrated are copied to the target database, you can run the OnData Encryption Tool to start the data migration and encryption process.  

### 2.2 Run OnData Encryption Tool
a. First please refer to the [**Use OnData Encryption Tool**](reference_doc#securedb/ondata_encryption/use_ondata_encryption_tool) documentation and run the OnData Encryption Tool all 
the way to the \`Encryption Setting\` page. On that page, select the \`Migrate and Encrypt Data\` option and click on the \`Proceed\` button.  

![Migrate and Encrypt Data](${ img5 })  

b. Establish a connection to the target database. The database user needs to have the necessary permissions as detailed above.  

![Connect to Target Database](${ img6 })  

c. Select the Database Link established above, which connects from the target database to the source database. Then select the list of schemas and tables 
to be migrated and encrypted, and click on the \`Proceed\` button.  

![Select Tables](${ img7 })  

d. The \`Migration In Progress\` screen will be displayed as the migration and encryption process is performed.  

![Migration In Progress](${ img8 })  

e. After the migration and encryption process is completed, the \`Migration Completed\` screen is displayed. You can access the log files from the screen.  

![Migration Completed](${ img9 })  

`
  
export default content;
